import { Component, Input, OnInit, Output, EventEmitter, AfterViewInit, OnDestroy } from '@angular/core';
import { IFilterDataModel, IFilterVisibilitySettings, ITissueIdList } from './reusable-filter.model';
import { DropDownSettings } from './reusable-filter-constants';
import { MemberService } from 'src/app/service/member.service';
import { CustomerService } from '@services/customer.service';
import { ServiceRequestService } from 'src/app/service/service-request.service';
import { ReusableFilterService } from './reusable-filter.service';
import { ILoggedInUserViewModel } from 'src/app/model/common.model';
import { Subject, Subscription, fromEvent } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { UpdatedSRService } from 'src/app/vendor-customer-sr/vendor-customer-sr.service';
import { DateFilters, DefaultGUID, RoleName, RegionOptions } from 'src/app/@shared/constants/Constant';
import { MemberPreferenceSettingsModel } from 'src/app/service/memeber.model';
import { DatePipe } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import * as timeZones from 'src/assets/api-data/timezoneList.json';
import { LocationService } from 'src/app/service/location.service';
import { IFilterViewModel } from 'src/app/model/report.model';

declare var $: any;

@Component({
  selector: 'app-reusable-filter',
  templateUrl: './reusable-filter.component.html',
  styleUrls: ['./reusable-filter.component.css'],
})
export class ReusableFilterComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() filterVisibilitySettings: IFilterVisibilitySettings;
  @Output() filterButtonEventEmitter = new EventEmitter<IFilterDataModel>(true);

  private $applyFilter: Subject<boolean> = new Subject();
  private _subscriptions: Subscription = new Subscription();
  public user: ILoggedInUserViewModel;
  public isCustomer: boolean;
  public memberPreferenceSetting: MemberPreferenceSettingsModel;
  public isShowMemberPreferenceSettingPopup: boolean = false;
  public currentCustomerBUId: string;
  public filterDropdownData: IFilterDataModel = {
    serviceTypeList: [],
    selectedServiceTypeList: [],
    customerList: [],
    selectedCustomerList: [],
    legStatusList: [],
    srStatusList: [],
    selectedLegStatusList: [],
    selectedSrStatusList: [],
    tripIdList: [],
    selectedTripIdList: [],
    unosList: [],
    selectedUnosList: [],
    tissueIdList: [],
    selectedTissueIdList: [],
    originLocationList: [],
    selectedOriginLocationList: [],
    destinationLocationList: [],
    selectedDestinationLocationList: [],
    timeZoneList: [],
    selectedTimeZone: null,
    fromDate: null,
    toDate: null,
  };
  public dropDownSettings = DropDownSettings;
  public dateFilters = DateFilters;
  public regionList = RegionOptions;

  public legStatusList = [
    {
      statusId: 0,
      statusName: 'Not Started',
    },
    {
      statusId: 1,
      statusName: 'Enroute',
    },
    {
      statusId: 2,
      statusName: 'On-Scene',
    },
    {
      statusId: 3,
      statusName: 'Picked up',
    },
    {
      statusId: 4,
      statusName: 'Dropped Off',
    },
    {
      statusId: 5,
      statusName: 'On-board',
    },
    {
      statusId: 6,
      statusName: 'Wheels Up',
    },
    {
      statusId: 7,
      statusName: 'Landed',
    },
    {
      statusId: 8,
      statusName: 'Driver Done',
    },
  ];

  constructor(
    private _reusableFilterService: ReusableFilterService,
    private _memberService: MemberService,
    private _customerService: CustomerService,
    private _serviceRequestService: ServiceRequestService,
    private _updatedSRService: UpdatedSRService,
    private _datePipe: DatePipe,
    private _toaster: ToastrService,
    private _locationService: LocationService,
  ) {
    this.user = JSON.parse(localStorage.getItem('user'));
    this.isCustomer = this.user.role == RoleName.CustomerAdmin || this.user.role == RoleName.CustomerCoordinator;
    this.memberPreferenceSetting = JSON.parse(localStorage.getItem('MemberPreferenceSettings'));
    this.currentCustomerBUId =
      this.user.role == 'Customer Admin' || this.user.role == 'Customer Coordinator' ? this.user.businessUnitId : DefaultGUID;
  }

  ngOnInit() {
    if (!this.filterVisibilitySettings.isActionButtonVisible) {
      this._subscriptions.add(
        this.$applyFilter.pipe(debounceTime(1000)).subscribe((flag: boolean) => {
          if (flag) {
            this.applyFilter();
          }
        }),
      );
    }
    this.filterDropdownData.legStatusList = this.legStatusList;
    this.filterDropdownData.timeZoneList = (timeZones as any).default;
    let date = new Date();
    let splitDate = date.toString().split('(')[1].split(')')[0];
    this.filterDropdownData.selectedTimeZone = this.filterDropdownData.timeZoneList.find((x) => x.matchingTimezoneName === splitDate);
    this.getAllServiceLocations();
    this.getCustomerList();
    this.getServiceTypes();
    this.getTripUnosIdList();
    this.getStatusList();
    this.getAllTissueId();
    if (
      this.filterVisibilitySettings &&
      this.filterVisibilitySettings.isShowFilterHeader &&
      this._updatedSRService.redirectionFilter.value == null
    ) {
      this.setMemberPreferenceDataToFilters();
    }
  }

  ngAfterViewInit() {
    this.setInputChangeEventForTripAndUnosList();
    this.initializeDatePicker();
  }

  ngOnDestroy() {
    this._subscriptions.unsubscribe();
    if (this.filterVisibilitySettings.isActionButtonVisible) {
      this._updatedSRService.redirectionFilter.next(null);
    }
  }

  setMemberPreferenceDataToFilters() {
    if (this.memberPreferenceSetting.fromDate != null) {
      this.filterDropdownData.selectedServiceTypeList = this.memberPreferenceSetting.serviceType
        ? this.memberPreferenceSetting.serviceType
        : [];
      this.filterDropdownData.selectedSrStatusList = this.memberPreferenceSetting.status ? this.memberPreferenceSetting.status : [];
      this.filterDropdownData.selectedOriginLocationList = this.memberPreferenceSetting.origin ? this.memberPreferenceSetting.origin : [];
      this.filterDropdownData.fromDate = this._datePipe.transform(this.setFromDate(this.memberPreferenceSetting.fromDate), 'MM/dd/yyyy');
      this._serviceRequestService.currentTimeZone.next(this.memberPreferenceSetting.selectedTimeZone.shortName);
    }
    this.applyFilter();
  }

  closeMemberPreferenceSettingPopup() {
    this.isShowMemberPreferenceSettingPopup = !this.isShowMemberPreferenceSettingPopup;
  }

  setInputChangeEventForTripAndUnosList() {
    const tripIdElem = document.getElementById('tripIdListDropdown');
    if (tripIdElem) {
      const tripIdFilterChangeEvent = fromEvent(tripIdElem, 'input');
      const delayedTripIdFilterChangeEvent = tripIdFilterChangeEvent.pipe(debounceTime(1000));
      delayedTripIdFilterChangeEvent.subscribe((event: any) => {
        this.callApiIfNoDataFound(event.target.value);
      });
    }

    const unosIdElem = document.getElementById('unosIdListDropdown');
    if (unosIdElem) {
      const unosIdFilterChangeEvent = fromEvent(unosIdElem, 'input');
      const delayedUnosIdFilterChangeEvent = unosIdFilterChangeEvent.pipe(debounceTime(2000));
      delayedUnosIdFilterChangeEvent.subscribe((event: any) => {
        this.callApiIfNoDataFound(event.target.value);
      });
    }
  }

  initializeDatePicker() {
    $('.fromDate').datetimepicker({
      timepicker: false,
      value: new Date(),
      maxDate: new Date(),
      format: 'm/d/Y',
      formatDate: 'Y/m/d',
      onChangeDateTime: () => {
        this.filterDropdownData.fromDate = $('#from').val();
        this.closeDatePicker('fromDate');
      },
    });

    $('.toDate').datetimepicker({
      timepicker: false,
      value: new Date(),
      maxDate: new Date(),
      format: 'm/d/Y',
      formatDate: 'Y/m/d',
      onChangeDateTime: () => {
        this.filterDropdownData.toDate = $('#to').val();
        this.closeDatePicker('toDate');
      },
    });
  }

  openDatePicker(flag: string) {
    if (flag == 'fromDate') {
      $('.fromDate').datetimepicker('show');
      $('.toDate').datetimepicker('hide');
    } else {
      $('.toDate').datetimepicker('show');
      $('.fromDate').datetimepicker('hide');
    }
  }

  closeDatePicker(flag) {
    if (flag == 'toDate') $('.toDate').datetimepicker('hide');
    else $('.fromDate').datetimepicker('hide');
  }

  clearDate(type) {
    if (type == 'from') {
      // $(".fromDate").datetimepicker("reset");
      this.filterDropdownData.fromDate = null;
    } else {
      // $(".toDate").datetimepicker("reset");
      this.filterDropdownData.toDate = null;
    }
  }

  clearFilterData() {
    this.filterDropdownData.toDate = null;
    this.filterDropdownData.fromDate = null;
    this.filterDropdownData.selectedDestinationLocationList = [];
    this.filterDropdownData.selectedOriginLocationList = [];
    this.filterDropdownData.selectedCustomerList = [];
    this.filterDropdownData.selectedTissueIdList = [];
    this.filterDropdownData.selectedTripIdList = [];
    this.filterDropdownData.selectedUnosList = [];
    this.filterDropdownData.selectedSrStatusList = [];
    this.filterDropdownData.selectedLegStatusList = [];
    this.filterDropdownData.selectedServiceTypeList = [];
  }

  getAllServiceLocations() {
    this._locationService.getServiceLocations().subscribe((response) => {
      this.filterDropdownData.originLocationList = response;
      this.filterDropdownData.destinationLocationList = response;
    });
  }

  getCustomerList(): void {
    this._subscriptions.add(
      this._customerService.getFilteredCustomerList().subscribe((response) => {
        this.filterDropdownData.customerList = response;
      }),
    );
  }

  getServiceTypes() {
    this._subscriptions.add(
      this._serviceRequestService.getServiceTypes().subscribe((response) => {
        this.filterDropdownData.serviceTypeList = response.filter((x) => x.serviceTypeName != 'Internal');
      }),
    );
  }

  getTripUnosIdList() {
    this._subscriptions.add(
      this._serviceRequestService.getFilterData(this.currentCustomerBUId).subscribe((response) => {
        this.setTripUnosListData(response);
      }),
    );
  }

  getAllTissueId() {
    this._subscriptions.add(
      this._serviceRequestService.getAllTissueId(this.currentCustomerBUId).subscribe(
        (response: ITissueIdList[]) => {
          this.removeDuplicateTissueIds(response);
        },
        (error) => console.log('unable to getTissue ID: ', error),
      ),
    );
  }
  private removeDuplicateTissueIds(arr: ITissueIdList[]) {
    this.filterDropdownData.tissueIdList = [];
    const tissueIdList: string[] = [...new Set(arr.map((x) => x.tissueId))];
    tissueIdList.map((item) => {
      this.filterDropdownData.tissueIdList.push(arr.filter((x) => x.tissueId == item)[0]);
    });
  }
  private setTripUnosListData(response: IFilterViewModel[]) {
    if (response) {
      try {
        this.filterDropdownData.tripIdList = [...new Set(response.map((x) => x.tripId))]
          .map((item, index) => {
            return { id: index, name: item };
          })
          .filter((x) => x.name != '');
        this.filterDropdownData.unosList = [...new Set(response.map((x) => x.unos))]
          .map((item, index) => {
            return { id: index, name: item };
          })
          .filter((x) => x.name != '');
      } catch (error) {
        console.log(error);
      }
    }
  }

  getStatusList() {
    this._subscriptions.add(
      this._serviceRequestService.getAllStatus().subscribe((response) => {
        this.filterDropdownData.srStatusList = response;
        this.subscribeFilter();
      }),
    );
  }

  private subscribeFilter() {
    const filter = this._updatedSRService.redirectionFilter.value;
    if (filter) {
      this.filterDropdownData.selectedSrStatusList = this.filterDropdownData.srStatusList.filter((item) => item.statusName == filter);
      this.applyFilter();
    }
  }

  toggleMoreFilter() {
    if (this.user.role == RoleName.VendorAdmin || this.user.role == RoleName.VendorCoordinator) {
      this.filterVisibilitySettings.customerDropdown = !this.filterVisibilitySettings.customerDropdown;
      this.filterVisibilitySettings.originLocationDropdown = !this.filterVisibilitySettings.originLocationDropdown;
    }
    $('#moreFilterIcon').toggleClass('is-expanded');
  }

  callApiIfNoDataFound(keywords: string) {
    this._reusableFilterService.callApiIfNoDataFound(keywords, this.currentCustomerBUId).then((value) => {
      this.setTripUnosListData(value);
    });
  }

  clearFilter() {
    this.clearFilterData();
    this.filterButtonEventEmitter.emit();
  }

  applyFilter() {
    const filterData: IFilterDataModel = {
      selectedCustomerList: this.filterDropdownData.selectedCustomerList.map((x) => x.customerID),
      selectedLegStatusList: this.filterDropdownData.selectedLegStatusList.map((x) => x.statusName),
      selectedSrStatusList: this.filterDropdownData.selectedSrStatusList.map((x) => x.statusName),
      selectedOriginLocationList: this.filterDropdownData.selectedOriginLocationList.map((x) => x.id),
      selectedDestinationLocationList: this.filterDropdownData.selectedDestinationLocationList.map((x) => x.id),
      selectedServiceTypeList: this.filterDropdownData.selectedServiceTypeList.map((x) => x.serviceTypeName),
      selectedTripIdList: this.filterDropdownData.selectedTripIdList.map((x) => x.name),
      selectedTissueIdList: this.filterDropdownData.selectedTissueIdList.map((x) => x.tissueId),
      selectedUnosList: this.filterDropdownData.selectedUnosList.map((x) => x.name),
      selectedTimeZone: this.filterDropdownData.selectedTimeZone,
      fromDate: this.filterDropdownData.fromDate != null ? new Date(this.filterDropdownData.fromDate) : null,
      toDate: this.filterDropdownData.toDate != null ? new Date(this.filterDropdownData.toDate) : null,
      originLocationList: [],
      destinationLocationList: [],
      customerList: [],
      serviceTypeList: [],
      legStatusList: [],
      srStatusList: [],
      tripIdList: [],
      unosList: [],
      tissueIdList: [],
      timeZoneList: [],
    };

    this.filterButtonEventEmitter.emit(filterData);
  }

  applyFilterIfAplicable() {
    if (!this.filterVisibilitySettings.isActionButtonVisible) {
      this.$applyFilter.next(true);
    }
  }

  saveMemberPreferenceSettings(memberSettings: MemberPreferenceSettingsModel) {
    this._subscriptions.add(
      this._memberService.saveMemberPreferenceSettings(this.user.memberId, memberSettings).subscribe(
        (response) => {
          this.isShowMemberPreferenceSettingPopup = false;
          this._serviceRequestService.currentTimeZone.next(memberSettings.selectedTimeZone.shortName);
          localStorage.setItem('MemberPreferenceSettings', JSON.stringify(memberSettings));
          this.memberPreferenceSetting = memberSettings;
          this.setMemberPreferenceDataToFilters();
          this._toaster.success('Settings Saved Successfully!', 'Success!', {
            timeOut: 6000,
            positionClass: 'toast-top-center',
          });
          // this.applyFilter();
        },
        (error) => {
          console.log(error);
          this._toaster.warning('Unable to save the filter settings. Please try again.', 'Warning', {
            timeOut: 6000,
            positionClass: 'toast-top-center',
          });
        },
      ),
    );
  }

  private setFromDate(data: string): Date {
    switch (data) {
      case 'Today': {
        return new Date();
      }
      case 'Last 24 Hours': {
        return new Date(Date.now() - 1000 * 60 * 60 * 24);
      }
      case 'Last 7 Days': {
        return new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      }
      case 'Last 30 Days': {
        return new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      }
      case 'Last 3 Months': {
        return new Date(Date.now() - 92 * 24 * 60 * 60 * 1000);
      }
      case 'Last 1 Year': {
        return new Date(Date.now() - 365 * 24 * 60 * 60 * 1000);
      }
      default: {
        return new Date();
      }
    }
  }
}
